import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface MinimaxT2v01DirectorParams {
  /**
   * 视频生成提示词，长度必须在1-2000个字符之间
   */
  prompt: string;
  /**
   * 视频时长（秒），T2V-01-Director固定为6秒
   * @default 6
   * @values 6
   */
  duration?: 6;
  /**
   * 视频分辨率，T2V-01-Director固定为720P
   * @default "720P"
   * @values 720P
   */
  resolution?: '720P';
  /**
   * 是否启用提示词优化器，默认为true
   * @default true
   */
  prompt_optimizer?: boolean;
}

export interface MinimaxT2v01DirectorResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * minimax_t2v_01_director
 *
 * 通过Minimax API创建文本到视频生成任务（导演模式）。[API Documentation](https://www.minimax.io/platform/document/text_to_video?key=68abdfffd08627aad9674bff)
 *
 * @example
 * ```typescript
 * import { initSeacloud, minimaxT2v01Director } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await minimaxT2v01Director({
 *   // 参数见 MinimaxT2v01DirectorParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 MinimaxT2v01DirectorParams 接口定义
 * @returns 生成的资源列表
 */
export async function minimaxT2v01Director(
  params: MinimaxT2v01DirectorParams,
): Promise<MinimaxT2v01DirectorResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'minimax_t2v_01_director',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: MinimaxT2v01DirectorResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
